import React, { useLayoutEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import SiteHeader from './components/layout/SiteHeader';
import SiteFooter from './components/layout/SiteFooter';
import ThematicBackground from './components/layout/ThematicBackground';
import SidebarLayout from './components/SidebarLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { loadRecaptchaScript } from './utils/recaptcha';
import { useAuth } from './components/User/AuthContext';
import { AdminEditProvider } from './context/AdminEditContext';
import './styles/GlobalBackground.css';

const Home = lazy(() => import('./Home'));
const AboutUsPage = lazy(() => import('./components/AboutUs/AboutUsPage'));
const ProgramsHubPage = lazy(() => import('./pages/ProgramsHubPage'));
const ProgramDetailPage = lazy(() => import('./pages/ProgramDetailPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const VolunteerPage = lazy(() => import('./pages/VolunteerPage'));
const ImpactPage = lazy(() => import('./pages/ImpactPage'));
const ImpactGalleryPage = lazy(() => import('./pages/ImpactGalleryPage'));
const DonatePage = lazy(() => import('./pages/DonatePage'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const AdminLogin = lazy(() => import('./components/Admin/AdminLogin'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="page-container text-center text-forest py-20" role="status">
      Loading…
    </div>
  );
}

function AdminRoute() {
  const { user, userData, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  if (!user || userData?.role !== 'admin') {
    return (
      <Suspense fallback={<PageLoader />}>
        <AdminLogin />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <AdminDashboard />
    </Suspense>
  );
}

function App() {
  React.useEffect(() => {
    loadRecaptchaScript();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AdminEditProvider>
          <ScrollToTop />
          <ErrorBoundary>
            <div className="relative min-h-screen flex flex-col text-forest">
              <ThematicBackground />
              <SiteHeader />
              <SidebarLayout>
                <main id="main-content" className="relative z-10 flex-1 w-full" tabIndex={-1}>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<AboutUsPage />} />
                      <Route path="/programs" element={<ProgramsHubPage />} />
                      <Route path="/programs/:slug" element={<ProgramDetailPage />} />
                      <Route path="/events" element={<EventsPage />} />
                      <Route path="/gallery" element={<Navigate to="/impact/gallery" replace />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/volunteer" element={<VolunteerPage />} />
                      <Route path="/impact" element={<ImpactPage />} />
                      <Route path="/impact/gallery" element={<ImpactGalleryPage />} />
                      <Route path="/donate" element={<DonatePage />} />
                      <Route path="/admin" element={<AdminRoute />} />
                      <Route path="/projects" element={<Navigate to="/impact" replace />} />
                      <Route path="/calendar" element={<Navigate to="/events" replace />} />
                      <Route path="/stories" element={<Navigate to="/blog" replace />} />
                      <Route path="/articles" element={<Navigate to="/blog" replace />} />
                      <Route path="/vision" element={<Navigate to="/events" replace />} />
                      <Route path="/contact" element={<Navigate to="/volunteer" replace />} />
                      <Route path="/profile" element={<Navigate to="/" replace />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </main>
              </SidebarLayout>
              <SiteFooter className="relative z-10" />
            </div>
          </ErrorBoundary>
        </AdminEditProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
